import { FC, Suspense, useState } from 'react';
import AdditionalResourceStuckIcon from './AdditionalResourceStuckIcon';
import { Button } from 'primereact/button';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import DigitalLessonModal from './DigitalLessonModal';
import { LessonsModal } from '../LessonsModal/LessonsModal';
import AdditionalResourceStuckStretchedIcon from './AdditionalResourceStuckStrechedIcon';
import { PreloadedQuery, useQueryLoader } from 'react-relay';
import { SkillResourceGetLessonsQuery } from '../../graphQLOperations/__generated__/SkillResourceGetLessonsQuery.graphql';
import { modalLessons } from '../../graphQLOperations/SkillResource';
import { SkillsLessonByTypeQuery } from '../../graphQLOperations/__generated__/SkillsLessonByTypeQuery.graphql';
import { getLessonsByType } from '../../graphQLOperations/Skills';

interface Props {
  isFixed: boolean;
  skillId: string;
}

const includedLessonTypesIds = [2, 6, 7, 8, 9, 10];

const AdditionalResources: FC<Props> = ({ isFixed, skillId }) => {
  const userRedux = useAppSelector(selectUser);
  const [digitalLessonsQueryRef, loadDigitalLessons] = useQueryLoader<SkillResourceGetLessonsQuery>(modalLessons);
  const [additionalLessonsQueryRef, loadAdditionalLessons] = useQueryLoader<SkillsLessonByTypeQuery>(getLessonsByType);

  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;

  const [openDigitalLessonModal, setOpenDigitalLessonModal] = useState<boolean>(false);
  const [openAdditionalLessonModal, setOpenAdditionalLessonModal] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [tabActive, setTabActive] = useState<boolean>(false);

  const handleDigitalLessonButton = () => {
    setOpenDigitalLessonModal(true);
    loadDigitalLessons({
      input: {
        skillId: skillId ? `SkillNode:${skillId}` : '',
        lessonNames: ['Skill Overview'],
      },
    });
  };

  const handleAdditionalLessonButton = () => {
    setOpenAdditionalLessonModal(true);
    loadAdditionalLessons({
      filter: {
        skillId: skillId ? `SkillNode:${skillId}` : '',
        lessonTypeIds: includedLessonTypesIds.map(lessonTypeId => `LessonTypeNode:${lessonTypeId}`),
        isActive: true,
      },
    });
  };

  return (
    <Suspense>
      <Button
        className={`nc_additional-resource-wrapper ${active || tabActive ? 'active' : ''} ${
          tabActive ? 'tab-active' : ''
        } ${isFixed ? 'fixed-active' : ''}  !tw-py-3 !tw-px-5 lg:!tw-p-6`}
        onClick={() => {
          setActive(!active);
          setTabActive(!tabActive);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setTabActive(!tabActive);
            setActive(!active);
          }
        }}
      >
        <AdditionalResourceStuckIcon
          className={'nc_stuck-icon tw-h-[48px] tw-w-[48px] lg:tw-h-[64px] lg:tw-w-[67px]'}
        />
        <AdditionalResourceStuckStretchedIcon
          className={'nc_stuck-stretched-icon tw-h-[48px] tw-w-[48px] lg:tw-h-[64px] lg:tw-w-[67px]'}
        />
        <span className="tw-font-atkinson tw-text-sm lg:tw-block tw-hidden tw-text-center tw-text-neutral-950 tw-font-light">
          Feeling stuck? <br></br>
          Try a little extra help
        </span>
        <div className="nc_additional-resource-buttons !tw-flex-col tw-gap-1">
          {!miamiBrand && (
            <>
              <Button
                label="Digital lesson"
                outlined
                onClick={() => handleDigitalLessonButton()}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDigitalLessonButton();
                  }
                }}
                className="tw-font-atkinson tw-text-sm tw-py-2 tw-px-4"
              />
              <span className="tw-font-atkinson tw-text-sm tw-text-neutral-950 tw-font-light">or</span>
            </>
          )}
          <Button
            label="See more resources"
            outlined
            onClick={() => handleAdditionalLessonButton()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleAdditionalLessonButton();
              }
            }}
            className="tw-font-atkinson tw-text-sm tw-py-2 tw-px-4"
          />
        </div>
      </Button>
      {openDigitalLessonModal && (
        <DigitalLessonModal
          visible={openDigitalLessonModal}
          setVisible={setOpenDigitalLessonModal}
          digitalLessonsQueryRef={
            digitalLessonsQueryRef as PreloadedQuery<SkillResourceGetLessonsQuery, Record<string, unknown>>
          }
        />
      )}
      {openAdditionalLessonModal && (
        <LessonsModal
          visible={openAdditionalLessonModal}
          setVisible={setOpenAdditionalLessonModal}
          customBranding={userRedux?.customBrandingApplied}
          additionalLessonsQueryRef={
            additionalLessonsQueryRef as PreloadedQuery<SkillsLessonByTypeQuery, Record<string, unknown>>
          }
        />
      )}
    </Suspense>
  );
};

export default AdditionalResources;
